<script setup>
/**
 * SuggestFeatureSheet — new-design ("fr-token") absorption of the legacy More
 * page's Suggestion modal (views/More/Shared/Modals/Suggestion.vue) into the
 * custom pass drawer. Same backend contract (api.misc.submitSuggestForm +
 * partnerUser.contact_permission), rebuilt as a bottom sheet that matches the
 * pass's design language instead of the Vuetify full-screen dialog.
 *
 * Real handoff, not dead UI: bound to the same store + API the legacy modal
 * used, so engineering only has to consume the existing endpoint.
 */
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { X, Check, Lightbulb } from '@lucide/vue'
import api from '@/api'

const props = defineProps({
  open: { type: Boolean, default: false },
  /** Style object of --fr-* CSS vars (required across the Teleport boundary). */
  theme: { type: Object, default: null }
})
const emit = defineEmits(['close'])

const store = useStore()
const offer = computed(() => store.state.offer)
const partner = computed(() => store.state.partner)
const partnerUser = computed(() => store.state.partnerUser)

const IMPORTANCE = ['Nice to Have', 'Important', 'Critical']

const suggestion = ref('')
const priority = ref('Nice to Have')
const contactPermission = ref(1)
const loading = ref(false)
const submitted = ref(false)

function reset() {
  suggestion.value = ''
  priority.value = 'Nice to Have'
  contactPermission.value = 1
  loading.value = false
  submitted.value = false
}

function close() {
  emit('close')
  // Reset after the slide-out so the form doesn't visibly flash empty.
  setTimeout(reset, 300)
}

async function submit() {
  if (!suggestion.value.trim() || loading.value) return
  loading.value = true
  try {
    // Persist the contact preference on the passholder (legacy modal parity).
    if (partnerUser.value?.id) {
      store.dispatch('partnerUser/setPartnerUser', {
        ...partnerUser.value,
        contact_permission: contactPermission.value
      })
    }
    await api.misc.submitSuggestForm({
      suggestion: suggestion.value,
      contactPermission: contactPermission.value,
      priority: priority.value,
      partnerName: partner.value?.name,
      partnerUserId: partnerUser.value?.id,
      offerId: offer.value?.id
    })
    submitted.value = true
  } catch (error) {
    console.error('Could not submit suggestion', error)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="sfs-overlay" :style="theme">
      <button
        id="giveaway_suggest_backdrop"
        type="button"
        aria-label="Close"
        class="sfs-backdrop gtm-click"
        @click="close"
      />
      <div class="sfs-sheet" role="dialog" aria-modal="true" aria-label="Suggest a feature">
        <!-- Success state -->
        <div v-if="submitted" class="sfs-body sfs-body--center">
          <div class="sfs-success-badge"><Check :size="22" /></div>
          <h3 class="sfs-title">Thanks for the idea!</h3>
          <p class="sfs-text">
            Your suggestion has been sent to our product team. We read every one — thanks for helping
            shape what comes next.
          </p>
          <button id="giveaway_suggest_done_btn" type="button" class="sfs-btn sfs-btn--primary gtm-click" @click="close">Done</button>
        </div>

        <!-- Form -->
        <div v-else class="sfs-body">
          <div class="sfs-head">
            <div class="sfs-head__titles">
              <div class="sfs-kicker"><Lightbulb :size="12" />Your idea</div>
              <h3 class="sfs-title">Suggest a feature</h3>
            </div>
            <button
              id="giveaway_suggest_close_btn"
              type="button"
              class="sfs-close gtm-click"
              aria-label="Close"
              @click="close"
            >
              <X :size="18" />
            </button>
          </div>

          <label class="sfs-label" for="giveaway_suggest_input">What would you like to be able to do?</label>
          <textarea
            id="giveaway_suggest_input"
            v-model="suggestion"
            class="sfs-textarea"
            rows="4"
            placeholder="Tell us what would make this pass better…"
          />

          <div class="sfs-label">How important is this to you?</div>
          <div class="sfs-chips">
            <button
              v-for="level in IMPORTANCE"
              :key="level"
              type="button"
              class="sfs-chip gtm-click"
              :class="{ 'is-active': priority === level }"
              @click="priority = level"
            >
              {{ level }}
            </button>
          </div>

          <div class="sfs-label">Can we contact you to hear more, if needed?</div>
          <div class="sfs-chips">
            <button
              type="button"
              class="sfs-chip gtm-click"
              :class="{ 'is-active': contactPermission === 1 }"
              @click="contactPermission = 1"
            >
              Yes
            </button>
            <button
              type="button"
              class="sfs-chip gtm-click"
              :class="{ 'is-active': contactPermission === 0 }"
              @click="contactPermission = 0"
            >
              No
            </button>
          </div>

          <button
            id="giveaway_suggest_submit_btn"
            type="button"
            class="sfs-btn sfs-btn--primary gtm-click"
            :disabled="!suggestion.trim() || loading"
            @click="submit"
          >
            {{ loading ? 'Sending…' : 'Submit suggestion' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.sfs-overlay {
  position: fixed;
  inset: 0;
  z-index: 2800; // above the drawer (2700)
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.sfs-backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(35, 47, 62, 0.65);
  cursor: pointer;
  animation: sfs-fade 150ms ease;
}
.sfs-sheet {
  position: relative;
  width: 100%;
  max-width: min(100dvw, var(--project-max-width, 480px));
  background: var(--fr-surface, #ffffff);
  color: var(--fr-ink, #1c1c1e);
  border-radius: 1.5rem 1.5rem 0 0;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  animation: sfs-slide-up 0.28s ease;
}
.sfs-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 85vh;
  overflow-y: auto;

  &--center {
    align-items: center;
    text-align: center;
  }
}
.sfs-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}
.sfs-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--fr-primary, #ff9900);
}
.sfs-title {
  margin: 0.25rem 0 0;
  font-family: var(--fr-font-serif-display, Georgia, serif);
  font-size: 1.125rem;
  font-weight: 900;
  line-height: 1.15;
}
.sfs-body--center .sfs-title { margin-top: 0; }
.sfs-text {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--fr-muted, #6b7280);
  line-height: 1.45;
}
.sfs-close {
  flex-shrink: 0;
  border: 0;
  background: none;
  color: var(--fr-muted, #6b7280);
  cursor: pointer;
  padding: 0.25rem;
  margin: -0.25rem -0.25rem 0 0;
}
.sfs-label {
  margin-top: 0.25rem;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fr-muted, #6b7280);
}
.sfs-textarea {
  width: 100%;
  resize: vertical;
  min-height: 96px;
  border-radius: 0.75rem;
  border: 1px solid color-mix(in srgb, var(--fr-ink, #1c1c1e) 15%, transparent);
  background: var(--fr-bg, #f6f4ef);
  padding: 0.625rem 0.75rem;
  font: inherit;
  font-size: 0.875rem;
  color: var(--fr-ink, #1c1c1e);
  outline: none;

  &:focus {
    border-color: var(--fr-primary, #ff9900);
    box-shadow: 0 0 0 3px rgba(var(--fr-primary-rgb, 255, 153, 0), 0.15);
  }
  &::placeholder { color: var(--fr-muted, #6b7280); opacity: 0.7; }
}
.sfs-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.sfs-chip {
  border: 1px solid color-mix(in srgb, var(--fr-ink, #1c1c1e) 15%, transparent);
  border-radius: 9999px;
  background: var(--fr-surface, #ffffff);
  padding: 0.375rem 0.75rem;
  font-size: 12px;
  font-weight: 700;
  color: var(--fr-ink, #1c1c1e);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover { background: var(--fr-bg, #f6f4ef); }
  &.is-active {
    background: var(--fr-primary, #ff9900);
    border-color: var(--fr-primary, #ff9900);
    color: var(--fr-accent, #232f3e);
  }
}
.sfs-btn {
  margin-top: 0.5rem;
  border: 0;
  cursor: pointer;
  border-radius: 0.75rem;
  padding: 0.75rem;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: transform 0.1s, opacity 0.15s;
  width: 100%;

  &:active { transform: scale(0.98); }
  &:disabled { opacity: 0.5; cursor: default; }

  &--primary { background: var(--fr-primary, #ff9900); color: var(--fr-accent, #232f3e); }
}
.sfs-success-badge {
  display: grid;
  place-items: center;
  height: 3rem;
  width: 3rem;
  border-radius: 9999px;
  background: #ecfdf5;
  color: #059669;
}

@keyframes sfs-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes sfs-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
</style>
